import { useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Clock, X, ChevronLeft, ChevronRight } from "lucide-react";
import { KineticText } from "../components/KineticText";
import { Reveal } from "../components/Reveal";
import { scrollToSection } from "../components/SmoothScroll";
import { SectionHeading } from "../components/SectionHeading";
import { Marquee } from "../components/Marquee";
import { CategoryCard } from "../components/CategoryCard";
import { CategoryModal } from "../components/CategoryModal";
import { Reviews } from "../components/Reviews";
import { InstagramFeed } from "../components/InstagramFeed";
import { SITE, WHATSAPP, CATALOGUE, PILLARS, GALLERY, DELIVERY_CITIES, getOpenStatus, waLink } from "../data/site";

const STATS = [
  { value: "1997", label: "Established" },
  { value: "20+", label: "Brand partners" },
  { value: "10+", label: "Categories" },
  { value: "7", label: "Days open" },
];

const TiltCard = () => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 140, damping: 18 });
  const status = getOpenStatus();

  return (
    <div className="[perspective:1400px]">
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width);
          my.set((e.clientY - r.top) / r.height);
        }}
        onMouseLeave={() => {
          mx.set(0.5);
          my.set(0.5);
        }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
        data-testid="hero-parallax-card"
      >
        <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-blue/20 shadow-[0_40px_100px_rgba(0,0,0,0.6)]">
          <img
            src={process.env.PUBLIC_URL + "/gallery/the-main-aisle.jpeg"}
            alt="The main aisle at the Ramaani Electricals experience centre"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
          <div className="pointer-events-none absolute inset-4 rounded-2xl border border-white/10" />
          <span className="absolute right-6 top-6 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70">
            EST. 1997
          </span>
        </div>
        <div
          className="absolute -bottom-5 left-6 flex items-center gap-3 rounded-full border border-white/10 bg-silt/90 px-5 py-3 backdrop-blur-xl"
          style={{ transform: "translateZ(50px)" }}
          data-testid="hero-open-status"
        >
          <span className={`h-2 w-2 rounded-full ${status.open ? "pulse-dot bg-wa" : "bg-red-400"}`} />
          <span className="text-xs text-cream">{status.label}</span>
        </div>
      </motion.div>
    </div>
  );
};

const Hero = () => {
  const { scrollY } = useScroll();
  const wmY = useTransform(scrollY, [0, 700], [0, 140]);

  return (
    <section id="home" className="relative overflow-hidden pt-40 sm:pt-44">
      <div className="pointer-events-none absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-blue/[0.07] blur-[140px]" />
      <motion.div
        style={{ y: wmY }}
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 left-0 select-none whitespace-nowrap font-display text-[16vw] font-semibold leading-none tracking-tight text-outline opacity-40"
      >
        RAMAANI
      </motion.div>

      <div className="mx-auto grid max-w-7xl gap-14 px-4 pb-24 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-32">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-blue"
          >
            Vijayawada · Andhra Pradesh · Since 1997
          </motion.p>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-[0.98] tracking-tight text-cream sm:text-5xl lg:text-6xl">
            <KineticText
              lines={["Everything", "Electrical.", "One Stop Shop."]}
              delay={0.15}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-sm leading-relaxed text-fog sm:text-base"
          >
            Wires, switchgear, lighting, fans and appliances — every brand that matters, out in the
            open at our Sheshaiah Street experience centre. Touch it, compare it, then take it home.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center"
          >
            <a
              href={waLink("Hi Ramaani Electricals, I'd like to visit the experience centre.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-blue px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.03] hover:bg-bluebright hover:shadow-[0_0_36px_rgba(46,107,255,0.4)]"
            >
              Chat on WhatsApp
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#products"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#products");
              }}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:scale-[1.03] hover:border-blue/50 hover:text-blue"
            >
              Browse the Catalogue
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 1 }}
            className="mt-14 grid grid-cols-2 gap-6 border-t border-white/10 pt-8 sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-2xl font-semibold text-cream sm:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <TiltCard />
        </div>
      </div>
    </section>
  );
};

const WhyChooseUs = () => (
  <section className="border-t border-white/10 py-24 sm:py-32" data-testid="why-choose-us-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        kicker="Why Choose Us"
        title="Why Vijayawada buys from Ramaani"
        copy="Four reasons generations of families, electricians and builders keep coming back to the same street."
      />
      <div className="mt-14 grid gap-5 sm:grid-cols-2">
        {PILLARS.slice(0, 4).map((p, i) => (
          <Reveal key={p.num} delay={i * 0.07}>
            <div className="group h-full rounded-3xl border border-white/10 bg-steel/50 p-8 transition-all duration-500 hover:border-blue/40 hover:bg-steel/80 hover:shadow-[0_0_44px_rgba(46,107,255,0.1)]">
              <span className="font-display text-5xl font-semibold leading-none text-outline transition-all duration-500 group-hover:[-webkit-text-stroke:1px_#2E6BFF]">
                {p.num}
              </span>
              <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-cream sm:text-2xl">
                {p.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-fog">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Products = () => {
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const list = expanded ? CATALOGUE : CATALOGUE.slice(0, 4);

  return (
    <section id="products" className="scroll-mt-28 border-t border-white/10" data-testid="products-section">
      <div className="py-14 sm:py-16">
        <Reveal className="mx-auto mb-8 max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blue">
            Brands We Deal In
          </p>
          <p className="mt-3 text-sm text-fog">
            Authorised dealer for the brands that build Vijayawada — genuine stock, straight from the source.
          </p>
        </Reveal>
        <Marquee />
      </div>

      <div className="border-t border-white/10 bg-silt/40 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            kicker="Product Portfolio"
            title="Everything electrical, under one roof"
            copy="10+ categories on display — tap any category for the full list, then come see it working first-hand."
          />
          <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <AnimatePresence mode="popLayout">
              {list.map((c, i) => (
                <CategoryCard key={c.id} category={c} index={i} onSelect={setActive} />
              ))}
            </AnimatePresence>
          </motion.div>
          <Reveal className="mt-12 text-center">
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="view-full-portfolio-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:scale-[1.03] hover:border-blue/50 hover:text-blue"
            >
              {expanded ? "Show Less" : "View Full Portfolio"}
            </button>
          </Reveal>
        </div>
      </div>
      <CategoryModal category={active} onClose={() => setActive(null)} />
    </section>
  );
};

const DeliverySection = () => (
  <section className="border-t border-white/10 py-24 sm:py-32" data-testid="delivery-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        kicker="Delivery Network"
        title="We deliver across Andhra Pradesh"
        copy="Walk into the Sheshaiah Street experience centre — or get your order delivered to any major city in the state."
      />
      <Reveal className="mt-12">
        <div className="rounded-3xl border border-blue/25 bg-blue/[0.05] p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue">Home Base</p>
          <p className="mt-2 font-display text-xl font-medium tracking-tight text-cream">
            Vijayawada — Experience Centre
          </p>
          <p className="mt-2 max-w-lg text-sm leading-relaxed text-fog">
            Rajgruhi, Ground Floor, Sheshaiah Street, One Town. Same-day delivery across the city.
          </p>
        </div>
      </Reveal>
      <div className="mt-8 flex flex-wrap gap-2.5">
        {DELIVERY_CITIES.map((city, i) => (
          <Reveal key={city} delay={i * 0.04}>
            <span
              data-testid={`delivery-city-${city.toLowerCase()}`}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-steel/50 px-5 py-2.5 text-sm text-cream/90 transition-colors duration-300 hover:border-blue/40"
            >
              <MapPin size={13} className="text-blue" /> {city}
            </span>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Gallery = () => {
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const photos = expanded ? GALLERY : GALLERY.slice(0, 4);

  return (
    <section id="gallery" className="scroll-mt-28 border-t border-white/10 py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="Inside the Experience Centre"
          title="Touch it first, then take it home"
          copy="Counters, shelves and demo walls — everything out in the open for you to touch and compare."
        />
        <div className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3">
          {photos.map((g, i) => (
            <Reveal key={g.title} delay={(i % 3) * 0.08} className="mb-5 break-inside-avoid">
              <button
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${i}`}
                className="group relative block w-full overflow-hidden rounded-3xl border border-white/10 text-left transition-all duration-500 hover:border-blue/40"
              >
                <img
                  src={g.src}
                  alt={g.title}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="font-display text-lg font-medium tracking-tight text-cream">
                    {g.title}
                  </p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="view-full-gallery-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:scale-[1.03] hover:border-blue/50 hover:text-blue"
          >
            {expanded ? "Show Less" : "View Full Gallery"}
          </button>
        </Reveal>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            data-testid="gallery-lightbox"
          >
            <div
              className="absolute inset-0 bg-obsidian/90 backdrop-blur-md"
              onClick={() => setActive(null)}
            />
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 w-full max-w-4xl"
            >
              <img
                src={GALLERY[active].src}
                alt={GALLERY[active].title}
                className="max-h-[72vh] w-full rounded-3xl border border-white/10 object-cover"
              />
              <figcaption className="mt-4 flex items-end justify-between gap-4">
                <p className="font-display text-xl font-medium text-cream">
                  {GALLERY[active].title}
                </p>
                <p className="font-mono text-xs text-ash">
                  {String(active + 1).padStart(2, "0")} / {String(GALLERY.length).padStart(2, "0")}
                </p>
              </figcaption>
            </motion.figure>

            <button
              onClick={() => setActive(null)}
              aria-label="Close gallery"
              data-testid="lightbox-close-button"
              className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-obsidian/60 text-cream backdrop-blur-md transition-colors hover:border-blue/60 hover:text-blue"
            >
              <X size={18} />
            </button>
            <button
              onClick={() => setActive((a) => (a - 1 + GALLERY.length) % GALLERY.length)}
              aria-label="Previous photo"
              data-testid="lightbox-prev-button"
              className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-obsidian/60 text-cream backdrop-blur-md transition-colors hover:border-blue/60 hover:text-blue"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => setActive((a) => (a + 1) % GALLERY.length)}
              aria-label="Next photo"
              data-testid="lightbox-next-button"
              className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-obsidian/60 text-cream backdrop-blur-md transition-colors hover:border-blue/60 hover:text-blue"
            >
              <ChevronRight size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const WhatsAppCard = () => (
  <Reveal>
    <div className="rounded-3xl border border-wa/25 bg-wa/[0.04] p-7 sm:p-10">
      <div className="grid items-center gap-8 lg:grid-cols-2">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-wa">Fastest Response</p>
          <h3 className="mt-3 font-display text-2xl font-medium tracking-tight text-cream sm:text-3xl">
            Planning a visit?
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-fog">
            Tell us what you're looking for on WhatsApp — we'll confirm stock and keep it ready at
            the counter, so you can see it working when you walk in.
          </p>
          <p className="mt-5 font-mono text-xs text-ash">{WHATSAPP.display}</p>
        </div>
        <div className="space-y-2.5">
          {WHATSAPP.presets.map((msg, i) => (
            <a
              key={i}
              href={waLink(msg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`contact-whatsapp-option-${i}`}
              className="group flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-steel/60 px-5 py-4 text-sm text-fog transition-all duration-300 hover:border-wa/50 hover:text-cream"
            >
              <span>{msg}</span>
              <ArrowUpRight
                size={15}
                className="shrink-0 text-wa transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          ))}
        </div>
      </div>
    </div>
  </Reveal>
);

const MapBlock = () => (
  <Reveal>
    <div className="overflow-hidden rounded-3xl border border-[#EA4335]/25 bg-[#EA4335]/[0.04]">
      <div className="grid lg:grid-cols-2">
        <div className="p-7 sm:p-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#EA4335]">Find Us</p>
          <h3 className="mt-3 font-display text-2xl font-medium tracking-tight text-cream sm:text-3xl">
            Walk in — we're on Sheshaiah Street
          </h3>
          <div className="mt-6 space-y-2.5 text-sm text-fog">
            <p className="flex items-start gap-2.5">
              <MapPin size={15} className="mt-0.5 shrink-0 text-[#EA4335]" /> {SITE.address}
            </p>
            <p className="flex items-center gap-2.5">
              <Clock size={15} className="shrink-0 text-[#EA4335]" /> {SITE.hours}
            </p>
            <p className="flex items-center gap-2.5 pl-6 text-xs text-ash">{SITE.hoursSunday}</p>
          </div>
          <a
            href={SITE.mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-directions-button"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#EA4335] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.03] hover:brightness-110"
          >
            Get Directions <ArrowUpRight size={15} />
          </a>
        </div>
        <div className="min-h-[280px]" data-testid="google-map-container">
          <iframe
            title="Ramaani Electricals location map"
            src={SITE.mapEmbed}
            className="map-dark h-full min-h-[280px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  </Reveal>
);

const Contact = () => (
  <section id="contact" className="scroll-mt-28 border-t border-white/10 py-24 sm:py-32" data-testid="contact-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        kicker="Contact & Visit"
        title="Say hello, then come see it"
        copy="WhatsApp us, follow the reels, read the reviews — then walk in and touch everything yourself."
      />
      <div className="mt-14 space-y-6">
        <WhatsAppCard />
      </div>
      <div className="mt-6">
        <InstagramFeed embedded />
      </div>
      <div className="mt-6">
        <Reviews embedded />
      </div>
      <div className="mt-6">
        <MapBlock />
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <WhyChooseUs />
      <Products />
      <DeliverySection />
      <Gallery />
      <Contact />
    </>
  );
}
